import React, { useMemo } from 'react';

import { SpecialsCategoryGrid } from 'components/specials-category-cards/specials-category-grid';
import { RemoteComponentSlot, useRemoteComponent } from 'hooks/use-remote-component';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';
import type { SpecialMenuCard } from 'src/gql/graphql';
import { useHideSalesLanguage } from 'src/hooks/use-hide-sales-language';
import useUI from 'src/hooks/use-ui';
import type { StoreFrontCategoryTilesProps } from '@dutchiesdk/ecommerce-extensions-sdk';

import { mapSpecialsCategoryTile, selectSpecialsCategories } from './specials-category-tiles-utils';

type SpecialsCategoryTilesProps = {
  loading: boolean;
  specials: SpecialMenuCard[];
};

export function SpecialsCategoryTiles({ loading, specials }: SpecialsCategoryTilesProps): JSX.Element | null {
  const { dispensary } = useDispensary();
  const { categories: dispensaryCategories } = useDispensaryCategories();
  const hideSalesLanguage = useHideSalesLanguage();
  const { dispensaryRootUrl } = useUI();

  const categories = useMemo(() => selectSpecialsCategories(specials, dispensaryCategories), [
    dispensaryCategories,
    specials,
  ]);
  const heading = hideSalesLanguage ? 'Shop Offers By Category' : 'Shop Specials By Category';

  const NativeCategoryTiles = useMemo(
    () =>
      function NativeSpecialsCategoryTiles(): JSX.Element | null {
        if (!dispensary) {
          return null;
        }

        return <SpecialsCategoryGrid categories={categories} dispensaryCName={dispensary.cName} heading={heading} />;
      },
    [categories, dispensary, heading]
  );

  const RemoteCategoryTiles = useRemoteComponent<StoreFrontCategoryTilesProps>(
    RemoteComponentSlot.SpecialsCategoryTiles,
    {
      fallback: NativeCategoryTiles,
    }
  );

  if (!dispensary) {
    return null;
  }

  if (loading) {
    return <SpecialsCategoryGrid categories={[]} dispensaryCName={dispensary.cName} heading={heading} loading />;
  }

  if (categories.length === 0) {
    return null;
  }

  const categoryTiles = categories.map((category) => mapSpecialsCategoryTile(category, dispensary, dispensaryRootUrl));

  return <RemoteCategoryTiles categories={categoryTiles} heading={heading} surface='specials' />;
}
